/**
 * JKL Cart Toast Notification
 * Replaces mini cart drawer with a toast notification
 * 
 * Features:
 * - Shows toast when product is added to cart
 * - Shows toast when cart icon is clicked
 * - Displays original free shipping progress bar design
 * - Displays original gift threshold promo design
 * - Link to cart page
 */

(function() {
  'use strict';

  // Configuration from data attribute
  let config = null;
  
  // Toast auto-hide timer
  let autoHideTimer = null;
  const AUTO_HIDE_DELAY = 8000; // 8 seconds (longer to allow viewing promos)

  function getToastLineItemType(item) {
    const p = item.properties;
    if (p == null) return null;
    if (typeof p === 'object' && !Array.isArray(p)) return p._type || null;
    if (Array.isArray(p)) {
      for (let i = 0; i < p.length; i++) {
        if (p[i].name === '_type') return p[i].value;
      }
    }
    return null;
  }

  function isToastLineExcludedFromTotals(item) {
    if (!item) return false;
    if (item.gift_card) return true;
    const t = getToastLineItemType(item);
    return t === 'gift' || t === 'giftThreshold' || t === 'promo';
  }

  function getToastDisplayedTotalCents(cart) {
    let excluded = 0;
    (cart.items || []).forEach((item) => {
      if (!isToastLineExcludedFromTotals(item)) return;
      excluded += item.final_line_price || 0;
    });
    return Math.max(0, (cart.total_price || 0) - excluded);
  }

  /**
   * Initialize Toast functionality
   */
  function init() {
    loadConfig();
    initToastControls();
    initAddToCartListeners();
    initCartIconListeners();
  }

  /**
   * Load configuration from JSON data attribute
   */
  function loadConfig() {
    const configEl = document.querySelector('[data-jkl-toast-config]');
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        // Config parsing failed, will use defaults
      }
    }
  }

  /**
   * Initialize toast open/close controls
   */
  function initToastControls() {
    // Use event delegation for close buttons
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-jkl-toast-close]');
      if (closeBtn) {
        e.preventDefault();
        hideToast();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const toast = document.querySelector('[data-jkl-cart-toast]');
        if (toast && toast.classList.contains('is-visible')) {
          hideToast();
        }
      }
    });
  }

  /**
   * Initialize listeners for add to cart events
   */
  function initAddToCartListeners() {
    // Listen for Shopify's native add to cart event
    document.addEventListener('cart:item-added', (e) => {
      showToastWithProduct(e.detail);
    });

    // Also listen for custom JKL event
    document.addEventListener('jkl:cart:item-added', (e) => {
      showToastWithProduct(e.detail);
    });

    // Intercept form submissions for add to cart
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (form.matches('form[action*="/cart/add"]') || form.matches('product-form form')) {
        // Don't prevent default - let the form submit normally
        // But prepare to show toast after
        const formData = new FormData(form);
        const variantId = formData.get('id');
        
        // Store variant info for toast
        if (variantId) {
          sessionStorage.setItem('jkl_pending_toast', JSON.stringify({
            variantId: variantId,
            timestamp: Date.now()
          }));
        }
      }
    });

    // Check for pending toast on page load (after redirect)
    checkPendingToast();
  }

  /**
   * Check for pending toast notification (after page redirect)
   */
  function checkPendingToast() {
    const pending = sessionStorage.getItem('jkl_pending_toast');
    if (pending) {
      try {
        const data = JSON.parse(pending);
        // Only show if less than 5 seconds old
        if (Date.now() - data.timestamp < 5000) {
          fetchCartAndShowToast(true);
        }
      } catch (e) {
        // Failed to parse pending toast
      }
      sessionStorage.removeItem('jkl_pending_toast');
    }
  }

  /**
   * Initialize cart icon click listeners
   */
  function initCartIconListeners() {
    // Use event delegation for cart icon clicks
    document.addEventListener('click', (e) => {
      const cartBtn = e.target.closest('[data-jkl-cart-open]');
      if (cartBtn) {
        // Let the link navigate directly to /cart (mobile and desktop)
        return;
      }
    });
  }

  /**
   * Show toast notification
   */
  function showToast(productData = null) {
    const toast = document.querySelector('[data-jkl-cart-toast]');
    if (!toast) return;

    // Update title based on context
    const titleEl = toast.querySelector('[data-jkl-toast-title]');
    if (titleEl) {
      if (productData) {
        titleEl.textContent = config?.translations?.itemAdded || 'Articolo aggiunto al carrello';
      } else {
        titleEl.textContent = config?.translations?.cartUpdated || 'Il tuo carrello';
      }
    }

    // Show/hide product section
    const productSection = toast.querySelector('[data-jkl-toast-product]');
    if (productSection) {
      if (productData) {
        updateProductSection(productSection, productData);
        productSection.style.display = 'flex';
      } else {
        productSection.style.display = 'none';
      }
    }

    // Show toast
    toast.style.display = 'flex';
    // Force reflow
    toast.offsetHeight;
    toast.classList.add('is-visible');
    document.body.style.overflow = 'hidden';

    // Clear existing timer
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
    }

    // Auto-hide after delay
    autoHideTimer = setTimeout(() => {
      hideToast();
    }, AUTO_HIDE_DELAY);
  }

  /**
   * Show toast with product information
   */
  function showToastWithProduct(productData) {
    // Check if we have valid product data
    const hasValidData = productData && (productData.title || productData.image || productData.price);
    
    if (!hasValidData) {
      // Fetch cart to get the last added item
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          updateCartSummary(cart);
          updateRelatedProductsCartData(cart);
          loadRelatedProducts();
          refreshShippingAndGift();

          if (cart.items && cart.items.length > 0) {
            const lastItem = cart.items[0];
            showToast({
              title: lastItem.product_title,
              variant: lastItem.variant_title,
              price: lastItem.final_line_price,
              image: lastItem.image
            });
          } else {
            showToast();
          }
        })
        .catch(() => showToast());
      return;
    }
    
    // Refresh the toast content by reloading the section
    refreshToastContent().then(() => {
      showToast(productData);
    }).catch(() => {
      showToast(productData);
    });
  }

  /**
   * Fetch cart and show toast (for cart icon click)
   */
  function fetchCartAndShowToast(showProduct = false) {
    fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    })
    .then(r => r.json())
    .then(cart => {
      updateCartSummary(cart);
      updateRelatedProductsCartData(cart);
      loadRelatedProducts();
      refreshShippingAndGift();

      if (showProduct && cart.items && cart.items.length > 0) {
        const lastItem = cart.items[0];
        showToast({
          title: lastItem.product_title,
          variant: lastItem.variant_title,
          price: lastItem.final_line_price,
          image: lastItem.image
        });
      } else {
        showToast();
      }
    })
    .catch(() => {
      showToast();
    });
  }

  /**
   * Re-render shipping bar and gift promo via Shopify Section Rendering API.
   * sections/jkl-cart-toast-section.liquid must exist for this to work.
   */
  function refreshShippingAndGift() {
    return fetch(`${window.location.pathname}?sections=jkl-cart-toast-section`)
      .then(r => r.json())
      .then(sections => {
        const html = sections['jkl-cart-toast-section'];
        if (!html) return;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const newShipping = tempDiv.querySelector('[data-jkl-toast-shipping-wrapper]');
        const currentShipping = document.querySelector('[data-jkl-toast-shipping-wrapper]');
        if (newShipping && currentShipping) {
          currentShipping.innerHTML = newShipping.innerHTML;
        }

        const newGift = tempDiv.querySelector('[data-jkl-toast-gift-wrapper]');
        const currentGift = document.querySelector('[data-jkl-toast-gift-wrapper]');
        if (newGift && currentGift) {
          currentGift.innerHTML = newGift.innerHTML;
        }
      })
      .catch(() => {/* silent — show toast even if section render fails */});
  }

  /**
   * Refresh toast content (shipping bar, gift promo, related products) via section rendering
   */
  function refreshToastContent() {
    return new Promise((resolve, reject) => {
      fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      })
      .then(r => r.json())
      .then(cart => {
        updateCartSummary(cart);
        updateRelatedProductsCartData(cart);

        refreshShippingAndGift().then(() => {
          loadRelatedProducts();
          resolve();
        });
      })
      .catch(reject);
    });
  }

  /**
   * Update the related products container with fresh cart data
   */
  function updateRelatedProductsCartData(cart) {
    const container = document.querySelector('[data-jkl-related-products]');
    if (!container) return;

    // Build cart product IDs and handles from cart data
    const cartProductIds = [];
    const cartProductHandles = [];
    
    for (const item of cart.items) {
      // Skip gift items (items with _type property)
      const isGift = item.properties && item.properties._type;
      if (!isGift) {
        cartProductIds.push(item.product_id.toString());
        cartProductHandles.push(item.handle);
      }
    }

    // Update data attributes
    container.dataset.cartProductIds = cartProductIds.join(',');
    container.dataset.cartProductHandles = cartProductHandles.join(',');
  }

  /**
   * Hide toast notification
   */
  function hideToast() {
    const toast = document.querySelector('[data-jkl-cart-toast]');
    if (!toast) return;

    toast.classList.remove('is-visible');
    document.body.style.overflow = '';

    // Clear auto-hide timer
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
      autoHideTimer = null;
    }

    // Hide after animation
    setTimeout(() => {
      toast.style.display = 'none';
    }, 300);
  }

  /**
   * Update product section with product data
   * Handles data from both cart/add.js response and cart.js items
   */
  function updateProductSection(section, data) {
    const imageEl = section.querySelector('[data-jkl-toast-product-image]');
    const titleEl = section.querySelector('[data-jkl-toast-product-title]');
    const variantEl = section.querySelector('[data-jkl-toast-product-variant]');
    const priceEl = section.querySelector('[data-jkl-toast-product-price]');
    const imageContainer = section.querySelector('.jkl-cart-toast__product-image');

    // Handle image - can be string URL or null
    if (imageEl) {
      const imageUrl = data.image || data.featured_image?.url || '';
      
      if (imageUrl) {
        // Ensure we have a properly sized image
        const sizedImage = imageUrl.includes('?') 
          ? imageUrl 
          : imageUrl.replace(/(\.[^.]+)$/, '_200x200$1');
        imageEl.src = sizedImage;
        imageEl.alt = data.title || data.product_title || '';
        imageEl.style.display = '';
        if (imageContainer) imageContainer.style.display = '';
      } else {
        // Hide image container if not available
        imageEl.style.display = 'none';
        if (imageContainer) imageContainer.style.display = 'none';
      }
    }

    // Handle title - can be 'title' or 'product_title'
    if (titleEl) {
      const title = data.title || data.product_title || '';
      titleEl.textContent = title;
    }

    // Handle variant - can be 'variant' or 'variant_title', skip "Default Title"
    if (variantEl) {
      const variantTitle = data.variant || data.variant_title || '';
      const showVariant = variantTitle && variantTitle !== 'Default Title';
      variantEl.textContent = showVariant ? variantTitle : '';
      variantEl.style.display = showVariant ? 'block' : 'none';
    }

    // Handle price - can be 'price' or 'final_line_price' (in cents)
    if (priceEl) {
      const price = data.price || data.final_line_price || data.line_price || 0;
      if (price) {
        priceEl.textContent = formatMoney(price);
        priceEl.style.display = '';
      } else {
        priceEl.style.display = 'none';
      }
    }
  }

  /**
   * Update cart summary (subtotal, items count)
   */
  function updateCartSummary(cart) {
    const subtotalEl = document.querySelector('[data-jkl-toast-subtotal]');
    const itemsEl = document.querySelector('[data-jkl-toast-items-count]');

    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(getToastDisplayedTotalCents(cart));
    }

    if (itemsEl && config?.translations?.itemsCount) {
      const count = cart.item_count;
      const template = count === 1 
        ? config.translations.itemsCount.one 
        : config.translations.itemsCount.other;
      itemsEl.textContent = template.replace('__N__', count);
    }
  }

  /**
   * Format money using Shopify's money format
   */
  function formatMoney(cents) {
    if (!config?.moneyFormat) {
      // Default format
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }

    const amount = (cents / 100).toFixed(2);
    const amountWithComma = amount.replace('.', ',');
    
    return config.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{ amount }}', amount)
      .replace('{{ amount_with_comma_separator }}', amountWithComma);
  }

  // =========================================================================
  // Related Products - Using Shopify's Product Recommendations API
  // Matches Hydrogen's PRODUCT_RECOMMENDATIONS_QUERY logic
  // =========================================================================

  /**
   * Load related products for the toast notification
   * Uses Shopify's /recommendations/products.json endpoint
   * 
   * Logic (matching Hydrogen cart.ts):
   * - For each non-gift product in cart, fetch recommendations
   * - If 1 product: show all its recommendations (up to limit)
   * - If multiple products: show first unique recommendation from each
   * - Filter out products already in cart
   */
  async function loadRelatedProducts() {
    const container = document.querySelector('[data-jkl-related-products]');
    if (!container) return;

    const grid = container.querySelector('[data-jkl-related-grid]');
    const loadingEl = container.querySelector('[data-jkl-related-loading]');
    const template = document.querySelector('[data-jkl-related-product-template]');
    
    if (!grid || !template) return;

    // Get cart product IDs from data attribute
    const cartProductIds = (container.dataset.cartProductIds || '').split(',').filter(Boolean);
    const limit = parseInt(container.dataset.limit || '2', 10);
    
    if (cartProductIds.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Show loading state
    if (loadingEl) loadingEl.style.display = 'flex';
    grid.innerHTML = '';

    try {
      // Fetch recommendations for each cart product (matching Hydrogen logic)
      const recommendations = [];
      const cartProductIdSet = new Set(cartProductIds.map(id => id.toString()));
      
      for (const productId of cartProductIds) {
        try {
          const url = `/recommendations/products.json?product_id=${productId}&limit=10&intent=related`;
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            if (data.products && data.products.length > 0) {
              // Filter out products already in cart
              const filtered = data.products.filter(p => !cartProductIdSet.has(p.id.toString()));
              recommendations.push(filtered);
            } else {
              recommendations.push([]);
            }
          } else {
            recommendations.push([]);
          }
        } catch (err) {
          recommendations.push([]);
        }
      }

      // Apply Hydrogen's distribution logic
      let productsToShow = [];
      const seenProductIds = new Set();

      if (recommendations.length === 1) {
        // Only one product in cart: show all its recommendations (up to limit)
        productsToShow = recommendations[0].slice(0, limit);
      } else if (recommendations.length > 1) {
        // Multiple products: show first unique recommendation from each (sideCart logic)
        // This matches Hydrogen's: recommendations.map(arr => arr.find(rec => rec && !cartProductIds.has(rec.id)))
        for (const recArray of recommendations) {
          for (const rec of recArray) {
            if (rec && !seenProductIds.has(rec.id.toString())) {
              productsToShow.push(rec);
              seenProductIds.add(rec.id.toString());
              break; // Only take first unique from each array
            }
          }
          if (productsToShow.length >= limit) break;
        }
      }

      // Hide loading
      if (loadingEl) loadingEl.style.display = 'none';

      // Render products
      if (productsToShow.length === 0) {
        container.style.display = 'none';
        return;
      }

      container.style.display = '';
      
      for (const product of productsToShow) {
        const productCard = renderProductCard(template, product);
        if (productCard) {
          grid.appendChild(productCard);
        }
      }

    } catch (err) {
      if (loadingEl) loadingEl.style.display = 'none';
      container.style.display = 'none';
    }
  }

  /**
   * Render a product card from the template
   */
  function renderProductCard(template, product) {
    if (!template || !product) return null;

    const clone = template.content.cloneNode(true);
    const card = clone.querySelector('.jkl-cart-related__product');
    if (!card) return null;

    // Get first available variant
    const variant = product.variants && product.variants.length > 0 
      ? product.variants.find(v => v.available) || product.variants[0]
      : null;

    if (!variant) return null;

    // Image
    const imageEl = card.querySelector('[data-jkl-related-image]');
    if (imageEl) {
      const imageUrl = product.featured_image 
        ? product.featured_image.replace(/(\.[^.]+)$/, '_400x400$1')
        : (product.images && product.images[0] ? product.images[0].replace(/(\.[^.]+)$/, '_400x400$1') : '');
      imageEl.src = imageUrl;
      imageEl.alt = product.title || '';
    }

    // Title
    const titleEl = card.querySelector('[data-jkl-related-title]');
    if (titleEl) {
      titleEl.textContent = product.title || '';
    }

    // Price
    const priceEl = card.querySelector('[data-jkl-related-price]');
    const comparePriceEl = card.querySelector('[data-jkl-related-compare-price]');
    
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    if (comparePriceEl && variant.compare_at_price && variant.compare_at_price > variant.price) {
      comparePriceEl.textContent = formatMoney(variant.compare_at_price);
      comparePriceEl.style.display = '';
      if (priceEl) {
        priceEl.classList.add('jkl-cart-related__price-sale');
        priceEl.classList.remove('jkl-cart-related__price-regular');
      }
    }

    // Add to cart button
    const addBtn = card.querySelector('[data-jkl-add-to-cart]');
    if (addBtn) {
      addBtn.dataset.variantId = variant.id;
      addBtn.dataset.productTitle = product.title || '';
      addBtn.dataset.productImage = imageEl ? imageEl.src : '';
      addBtn.dataset.productPrice = variant.price;
      
      if (!variant.available) {
        addBtn.disabled = true;
      }
    }

    return card;
  }

  // =========================================================================
  // Add to Cart functionality for related products
  // =========================================================================

  /**
   * Initialize add to cart buttons (for related products, etc.)
   */
  function initAddToCartButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-jkl-add-to-cart]');
      if (!btn) return;
      
      e.preventDefault();
      
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      
      // Disable button during request
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = '...';
      
      // Add to cart via AJAX
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1
          }]
        })
      })
      .then(r => r.json())
      .then(response => {
        if (response.status) {
          btn.textContent = originalText;
          btn.disabled = false;
          return;
        }
        
        // Success - show toast with product info
        const addedItem = response.items ? response.items[0] : null;
        if (addedItem) {
          showToastWithProduct({
            title: addedItem.product_title,
            variant: addedItem.variant_title,
            price: addedItem.final_line_price,
            image: addedItem.image
          });
        } else {
          // Fallback: use data attributes
          showToastWithProduct({
            title: btn.dataset.productTitle || '',
            variant: '',
            price: parseInt(btn.dataset.productPrice || '0', 10),
            image: btn.dataset.productImage || ''
          });
        }
        
        // Fetch cart to get updated item_count (cart/add.js doesn't return it)
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(r => r.json())
          .then(cart => {
            updateCartCount(cart.item_count);
          })
          .catch(() => {
            // Fallback: don't update count if fetch fails
          });
        
        // Re-enable button
        btn.textContent = originalText;
        btn.disabled = false;
      })
      .catch(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      });
    });
  }

  /**
   * Update cart count badges in header
   */
  function updateCartCount(count) {
    if (count === undefined || count === null) return;
    
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // =========================================================================
  // Expose API for external use
  // =========================================================================

  window.JKLCartToast = {
    show: showToast,
    showWithProduct: showToastWithProduct,
    hide: hideToast,
    refresh: fetchCartAndShowToast,
    updateCartCount: updateCartCount
  };

  // Also expose as JKLCart for backward compatibility with header buttons
  window.JKLCart = {
    open: function() {
      showToast();
    },
    close: hideToast,
    refresh: fetchCartAndShowToast
  };

  // =========================================================================
  // Initialize on DOM ready
  // =========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initAddToCartButtons();
    });
  } else {
    init();
    initAddToCartButtons();
  }

  // Re-initialize after Shopify section rendering
  document.addEventListener('shopify:section:load', () => {
    init();
    initAddToCartButtons();
  });

})();
